import { useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Stack,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Tooltip,
  IconButton,
  FormHelperText,
  Autocomplete,
} from "@financeable-com-au/financeable-ui";
import { getIsCommercialOrBusinessApplication, envVariables, frequencyTypes, additionalManualQuoteLenderList } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  InArrears,
  ApplicationType,
  RepaymentFrequency,
} from "@/store/slices/types/applicationFormSlice";
import { DecimalMaskFormat } from "@/utils/DecimalMaskFormat";
import { defaultRepaymentFeeFrequency, getFormattedMonthlyRepayments } from "@/utils/lenderUtils";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
// @ts-expect-error: ignoring as it is existing js file
import commercialProducts from "@/products/commercial";
// @ts-expect-error: ignoring as it is existing js file
import consumerProducts from "@/products/consumer";
import type { LenderProduct } from "@/providers/LenderCardContextProvider/types/LenderProductTypes";

interface ManualQuoteFieldsProps {
  saveQuote: ({ actionFor, value, key }: { actionFor: string; value?: string; key?: string }) => void;
}

const ManualQuoteFields = ({ saveQuote }: ManualQuoteFieldsProps) => {
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const { lenderCard, quoteCalculatorFormik } = useLenderCardContext();
  const brokerage = lenderCard?.brokerage;
  const { applicationType } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);
  const isMonthlyRepaymentFrequency = quoteCalculatorFormik?.values.repaymentFrequency === frequencyTypes.Monthly;

  // Lenders list.
  let lendersList: Pick<LenderProduct, "lender">[] = [];
  if (application?.applicationType === ApplicationType.Commercial) {
    lendersList = [...new Set(commercialProducts.map((l: LenderProduct) => l.lender))].map((name) => ({
      lender: name as string,
    }));
  } else {
    lendersList = [...new Set(consumerProducts.map((l: LenderProduct) => l.lender))].map((name) => ({
      lender: name as string,
    }));
  }

  let filteredLenderList =
    lendersList?.length > 0
      ? lendersList.flat().filter((product) => envVariables.VITE_ALLOWED_LENDER_LIST.includes(product.lender))
      : [];

  filteredLenderList = [...filteredLenderList, ...additionalManualQuoteLenderList.map((lender) => ({ lender }))];

  // Deduplicate based on the lender name
  filteredLenderList = Array.from(new Map(filteredLenderList.map((item) => [item.lender?.toLowerCase()?.trim(), item])).values());

  // Sort list
  filteredLenderList.sort((a, b) => a.lender.localeCompare(b.lender));

  return (
    <Stack direction="column" rowGap={3}>
      <FormControl>
        <FormLabel>Lender</FormLabel>
        <Autocomplete
          placeholder="Search or select lender..."
          options={filteredLenderList.map((lender) => ({ label: lender.lender, value: lender.lender }))}
          value={filteredLenderList.find((l) => l.lender === quoteCalculatorFormik?.values.lender)?.lender ?? null}
          onChange={(_e, option) => {
            quoteCalculatorFormik?.setFieldValue("lender", (option as { value: string } | null)?.value ?? "");
          }}
          data-testid="select-lender-autocomplete"
        />
      </FormControl>
      <FormControl error={!!quoteCalculatorFormik?.errors.rate}>
        <FormLabel>Rate</FormLabel>
        <Input
          type="text"
          endDecorator="%"
          name="rate"
          value={quoteCalculatorFormik?.values.rate}
          onChange={quoteCalculatorFormik?.handleChange}
          onBlur={(e) => {
            quoteCalculatorFormik?.handleBlur(e);
            saveQuote({ actionFor: "rate" });
          }}
          slotProps={{
            input: {
              component: DecimalMaskFormat,
            },
          }}
        />
        {!!quoteCalculatorFormik?.errors.rate && (
          <FormHelperText>
            <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
            {quoteCalculatorFormik?.errors.rate}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl error={!!quoteCalculatorFormik?.errors.effectiveRate}>
        <FormLabel>Effective Rate</FormLabel>
        <Input
          type="text"
          endDecorator="%"
          name="effectiveRate"
          value={quoteCalculatorFormik?.values.effectiveRate}
          onChange={quoteCalculatorFormik?.handleChange}
          onBlur={(e) => {
            quoteCalculatorFormik?.handleBlur(e);
            saveQuote({ actionFor: "effectiveRate" });
          }}
          slotProps={{
            input: {
              component: DecimalMaskFormat,
            },
          }}
        />
        {!!quoteCalculatorFormik?.errors.effectiveRate && (
          <FormHelperText>
            <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
            {quoteCalculatorFormik?.errors.effectiveRate}
          </FormHelperText>
        )}
      </FormControl>
      {isCommercialOrBusinessApplication && (
        <FormControl error={!!quoteCalculatorFormik?.errors.brokerage}>
          <FormLabel>
            Brokerage
            {/* Tooltip to explain the max brokerage. */}
            {!!brokerage?.percentage?.value && !!brokerage?.max?.value && (
              <Tooltip
                title={
                  <Stack padding={2} flexDirection="column" rowGap={2}>
                    <Typography>
                      There is a Maximum Brokerage of <Typography bold>{brokerage.max.formattedValue}</Typography>
                    </Typography>
                  </Stack>
                }
              >
                <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                  <InfoIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </FormLabel>
          <Input
            type="text"
            name="brokerage"
            endDecorator="%"
            value={quoteCalculatorFormik?.values.brokerage}
            onChange={quoteCalculatorFormik?.handleChange}
            onBlur={(e) => {
              quoteCalculatorFormik?.handleBlur(e);
              saveQuote({ actionFor: "brokerage" });
            }}
            slotProps={{
              input: {
                component: DecimalMaskFormat,
              },
            }}
          />
          {!!quoteCalculatorFormik?.errors.brokerage && (
            <FormHelperText>
              <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
              {quoteCalculatorFormik?.errors.brokerage}
            </FormHelperText>
          )}
        </FormControl>
      )}
      <FormControl>
        <FormLabel>In Arrears</FormLabel>
        <Select
          value={quoteCalculatorFormik?.values.inArrears}
          name="inArrears"
          onChange={(_e, value) => {
            if (!!value) {
              quoteCalculatorFormik?.setFieldValue("inArrears", value);
              saveQuote({ actionFor: "inArrears", value: value as unknown as InArrears });
            }
          }}
        >
          <Option value={InArrears.Yes}>Yes</Option>
          <Option value={InArrears.No}>No</Option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>
          {quoteCalculatorFormik?.values?.repaymentFrequency} Repayments{" "}
          {!isMonthlyRepaymentFrequency
            ? `(${getFormattedMonthlyRepayments({
              repaymentsValue: quoteCalculatorFormik?.values.repayments ?? 0,
              repaymentFrequency: quoteCalculatorFormik?.values.repaymentFrequency as
                | RepaymentFrequency.Weekly
                | RepaymentFrequency.Fortnightly,
            })})`
            : ""}
        </FormLabel>
        <Input
          type="text"
          endDecorator="$"
          name="repayments"
          value={quoteCalculatorFormik?.values.repayments}
          onChange={quoteCalculatorFormik?.handleChange}
          onBlur={(e) => {
            quoteCalculatorFormik?.handleBlur(e);
            saveQuote({ actionFor: "repayments" });
          }}
          slotProps={{
            input: {
              component: DecimalMaskFormat,
            },
          }}
        />
        {!!quoteCalculatorFormik?.errors.repayments && (
          <FormHelperText>
            <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
            {quoteCalculatorFormik?.errors.repayments}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl>
        <FormLabel>Repayment Frequency</FormLabel>
        <Select
          value={quoteCalculatorFormik?.values.repaymentFrequency}
          name="repaymentFrequency"
          onChange={(_e, value) => {
            if (value) {
              quoteCalculatorFormik?.setFieldValue("repaymentFrequency", value);
              saveQuote({ actionFor: "repaymentFrequency", value: value as unknown as RepaymentFrequency });
            }
          }}
        >
          {defaultRepaymentFeeFrequency.map((frequency) => (
            <Option value={frequency} key={frequency}>
              {frequency}
            </Option>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

export { ManualQuoteFields };
